import { ILocalesResponse } from '@joc/api-gateway';

export type SupportedLanguageKey = 'en' | 'fr' | 'he' | 'ru' | 'uk';

export interface LanguageConfig {
	key: SupportedLanguageKey;
	label: string;
	required: boolean;
	dir: 'ltr' | 'rtl';
	defaultLocaleId: number;
}

export const SUPPORTED_LANGUAGES: readonly LanguageConfig[] = [
	{ key: 'en', label: 'English', required: true, dir: 'ltr', defaultLocaleId: 1 },
	{ key: 'he', label: 'Hebrew', required: false, dir: 'rtl', defaultLocaleId: 2 },
	{ key: 'fr', label: 'French', required: false, dir: 'ltr', defaultLocaleId: 3 },
	{ key: 'uk', label: 'Ukrainian', required: false, dir: 'ltr', defaultLocaleId: 4 },
	{ key: 'ru', label: 'Russian', required: false, dir: 'ltr', defaultLocaleId: 5 },
];

export type LocalizedStringsMap = Record<SupportedLanguageKey, string>;

export const createEmptyLocalizedMap = (): LocalizedStringsMap => ({
	en: '',
	fr: '',
	he: '',
	ru: '',
	uk: '',
});

export const matchLanguageKey = (
	locale: Pick<ILocalesResponse, 'localeShortName' | 'localeName'>
): SupportedLanguageKey | undefined => {
	const short = (locale.localeShortName || '').trim().toLowerCase();
	const name = (locale.localeName || '').trim().toLowerCase();

	if (short === 'en' || short.startsWith('en-') || short.startsWith('en_') || name.includes('english')) {
		return 'en';
	}
	if (
		short === 'fr' ||
		short.startsWith('fr-') ||
		short.startsWith('fr_') ||
		name.includes('french') ||
		name.includes('français')
	) {
		return 'fr';
	}
	if (
		short === 'he' ||
		short === 'iw' ||
		short.startsWith('he-') ||
		short.startsWith('he_') ||
		name.includes('hebrew') ||
		name.includes('עברית')
	) {
		return 'he';
	}
	if (
		short === 'ru' ||
		short.startsWith('ru-') ||
		short.startsWith('ru_') ||
		name.includes('russian') ||
		name.includes('русский')
	) {
		return 'ru';
	}
	if (
		short === 'uk' ||
		short === 'ua' ||
		short.startsWith('uk-') ||
		short.startsWith('uk_') ||
		name.includes('ukrainian') ||
		name.includes('українська')
	) {
		return 'uk';
	}

	return undefined;
};

export const buildLocaleIdMap = (locales?: ILocalesResponse[]): Record<SupportedLanguageKey, number> => {
	const map: Record<SupportedLanguageKey, number> = {
		en: 1,
		he: 2,
		fr: 3,
		uk: 4,
		ru: 5,
	};

	if (!locales?.length) {
		return map;
	}

	locales.forEach(locale => {
		const matchedKey = matchLanguageKey(locale);
		const parsedId = Number(locale.id);
		if (matchedKey && !Number.isNaN(parsedId) && parsedId > 0) {
			map[matchedKey] = parsedId;
		}
	});

	return map;
};

export const buildDirectLanguageKeyMap = (
	locales?: ILocalesResponse[]
): Record<number | string, SupportedLanguageKey> => {
	const reverseMap: Record<number | string, SupportedLanguageKey> = {};
	locales?.forEach(locale => {
		const matchedKey = matchLanguageKey(locale);
		const parsedId = Number(locale.id);
		if (matchedKey && !Number.isNaN(parsedId) && parsedId > 0) {
			reverseMap[parsedId] = matchedKey;
			reverseMap[String(parsedId)] = matchedKey;
		}
	});
	return reverseMap;
};

export const buildIdToLanguageKeyMap = (
	localeIdMap: Record<SupportedLanguageKey, number>
): Record<number | string, SupportedLanguageKey> => {
	const reverseMap: Record<number | string, SupportedLanguageKey> = {};
	SUPPORTED_LANGUAGES.forEach(({ key }) => {
		const id = localeIdMap[key];
		if (typeof id === 'number' && !Number.isNaN(id)) {
			reverseMap[id] = key;
			reverseMap[String(id)] = key;
		}
	});
	return reverseMap;
};
